// Which restaurant this dashboard tab loaded (UC-64).
//
// A Bosla admin's visit replaces the browser's owner sign-in, and so does an
// owner who signs in as another restaurant in another tab. A tab loaded
// before that must not act on a restaurant it never loaded. So once the tab
// has its restaurant, every /api/owner/ call names it in X-Bosla-Account, and
// the API answers 409 ACCOUNT_CHANGED when the session's restaurant is
// another. The tab then reloads, and loads the restaurant the browser is now
// signed in to, with no header on its first call.
//
// Only /api/owner/ calls carry it: the API checks it there alone, and the
// till's and the admin's calls never send it.

import type { Middleware } from 'openapi-fetch';

export const ACCOUNT_HEADER = 'X-Bosla-Account';

let loaded = '';

/** The tab has its restaurant: name it on every owner call from now on. */
export function tabLoaded(accountId: string): void {
	loaded = accountId;
}

/** The tab is signed out, or has no restaurant: name none. */
export function tabForgotten(): void {
	loaded = '';
}

/** The restaurant this tab names, or empty before it has one. */
export function tabAccount(): string {
	return loaded;
}

/** Whether a request URL is an owner API call. */
export function isOwnerCall(url: string): boolean {
	try {
		return new URL(url).pathname.startsWith('/api/owner/');
	} catch {
		return false;
	}
}

/** Whether an answer says the tab's restaurant is not the session's. */
export function accountChanged(status: number, body: unknown): boolean {
	if (status !== 409 || typeof body !== 'object' || body === null) return false;
	return (body as { code?: unknown }).code === 'ACCOUNT_CHANGED';
}

/** The client's middleware: names the restaurant, and reloads once it changed. */
export function createTabWatch(reload: () => void, account: () => string = tabAccount): Middleware {
	let reloading = false;
	return {
		onRequest({ request }) {
			const id = account();
			if (id && isOwnerCall(request.url)) request.headers.set(ACCOUNT_HEADER, id);
			return request;
		},
		async onResponse({ request, response }) {
			if (response.status !== 409 || !isOwnerCall(request.url)) return;
			try {
				if (!accountChanged(response.status, await response.clone().json())) return;
			} catch {
				return; // Not JSON: not this refusal.
			}
			if (!reloading) {
				reloading = true;
				reload();
			}
			// The page is going: no refused call answers its screen, which
			// would show an error over what is about to reload. A dashboard
			// reads several things at once, so each refusal is held.
			return new Promise<Response>(() => {});
		},
	};
}

export const watchTabAccount: Middleware = createTabWatch(() => window.location.reload());
